A
<template>
  <div v-loading="loading" class="space-y-8">
    <div class="bg-white border-brand-sand border-2 rounded-md py-6 px-6" style="min-height: 256px">
      <div class="">
        <div class="pt-2 px-2">
          <p class="title">Feature Flags</p>
          <p class="subtitle mt-1">Enable and disable features per org</p>
        </div>
      </div>
      <div v-if="orgs && orgs.length" class="w-full my-4">
        <!-- show a drodown to select org, labeled with orgname, created with element-ui -->
        <el-select filterable v-model="selectedOrgId" placeholder="Select an organization" class="">
          <el-option v-for="org in orgs" :key="org._id" :label="`${org.name}`" :value="org._id">
            <!-- template for showing recentRevFormatted and org name -->
            <span class="float-left text-sm">{{ org.name }}</span>
          </el-option>
        </el-select>
        <div v-if="selectedOrg" class="mt-8 grid grid-cols-3 gap-x-6">
          <div v-for="feature in features" :key="feature.key" class="bg-white shadow rounded-lg p-4 mb-4">
            <div class="">
              <div>
                <h3 class="text-lg font-semibold text-gray-800">{{ feature.label }}</h3>
                <p class="text-sm mt-1 font-medium text-gray-600">{{ feature.description }}</p>
              </div>
              <div class="mt-4">
                <el-switch
                  :value="feature.getValue(selectedOrg)"
                  active-text="Enabled"
                  inactive-text="Disabled"
                  :disabled="feature.locked && feature.value"
                  @change="(value) => setOrgSetting(feature.key, value)"
                ></el-switch>
              </div>
            </div>
          </div>
        </div>
        <div v-if="selectedOrg" class="mt-8 grid grid-cols-3 gap-x-6">
          <div v-for="setting in valueSettings" :key="setting.key" class="bg-white shadow rounded-lg p-4 mb-4">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">{{ setting.label }}</h3>
              <p class="text-sm mt-1 font-medium text-gray-600">{{ setting.description }}</p>
            </div>
            <div class="mt-4">
              <el-input v-model="setting.workingValue" :disabled="!setting.isEditing" :placeholder="setting.getValue(selectedOrg)"></el-input>
              <div class="mt-3">
                <el-button v-if="!setting.isEditing" size="small" type="primary" @click="makeValueSettingEditable(setting)">Edit</el-button>
                <el-button v-if="setting.isEditing" size="small" type="primary" @click="saveValueSetting(setting)">Save</el-button>
                <el-button v-if="setting.isEditing" size="small" type="text" @click="discardValueSetting(setting)">Discard Changes</el-button>
                <template v-if="setting.buttons">
                  <el-button
                    v-for="button in setting.buttons"
                    :key="button.label"
                    size="small"
                    type="text"
                    @click="button.action(selectedOrg)"
                    class="ml-2"
                    >{{ button.label }}</el-button
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ADMIN_SET_ORG_SETTING } from '@/store/action-types';

const features = [
  {
    key: 'reactivation',
    label: 'Reactivation Campaigns Access',
    description: 'Does org have access to reactivation campaigns?',
    getValue: (org) => org.reactivation?.enabled,
  },
  {
    key: 'dunning',
    label: 'Dunning Campaigns Activated',
    description: 'Are dunning campaigns fully activated for org?',
    getValue: (org) => org.dunning?.enabled,
    locked: true,
  },
  {
    key: 'precisionRetries',
    label: 'Precision Retries Activated',
    description: 'Are precision retries enabled for this org?',
    getValue: (org) => org.smartRetries?.enabled,
  },
];

const valueSettings = [
  {
    key: 'stripeCustomerId',
    label: 'Stripe Customer ID',
    description: 'The Stripe Customer ID for this organization',
    getValue: (org) => org.stripeCustomerId,
    buttons: [
      {
        label: 'View in Stripe',
        action: (org) => {
          window.open(`https://dashboard.stripe.com/customers/${org.stripeCustomerId}`, '_blank');
        },
      },
    ],
  },
  {
    key: 'orgName',
    label: 'Org Name',
    description: 'The org name shown internally and to end customers on hosted pages',
    getValue: (org) => org.name,
  },
];

export default {
  name: 'AdminFeatures',
  components: {},
  data() {
    return {
      loading: true,
      selectedOrgId: null,
      features,
      valueSettings: valueSettings.map((setting) => ({ ...setting, isEditing: false, workingValue: '' })),
    };
  },
  async created() {
    this.loading = false;
  },
  computed: {
    orgs() {
      return this.$store.state.orgs || [];
    },
    selectedOrg() {
      return this.orgs.find((org) => org._id === this.selectedOrgId);
    },
  },
  methods: {
    async makeValueSettingEditable(setting) {
      setting.isEditing = true;
      setting.workingValue = setting.getValue(this.selectedOrg);
      // this.$set(setting, 'workingValue', setting.getValue(this.selectedOrg));
    },
    async saveValueSetting(setting) {
      setting.isEditing = false;
      await this.setOrgSetting(setting.key, setting.workingValue);
    },
    async discardValueSetting(setting) {
      setting.isEditing = false;
      setting.workingValue = setting.getValue(this.selectedOrg);
    },
    async setOrgSetting(key, value) {
      const orgId = this.selectedOrgId;
      await this.$store.dispatch(ADMIN_SET_ORG_SETTING, { orgId, setting: key, value });
      this.$message({
        message: `Feature flag ${key} set to ${value}`,
        type: 'success',
      });
    },
  },
};
</script>
